import { useEffect, useRef } from 'react';

const ClickHandlerNodeDepthSearch = 5;

function findClickedCell(e:MouseEvent, tableId:string) {
  if (!e || !e.target) {
    return null;
  }

  const cellIdPrefix = `${tableId}-cell-`;

  let node = e.target as Element;
  for (let i = 0; i < ClickHandlerNodeDepthSearch; ++i) {
    if (!node) {
      break;
    }

    if (node.id && node.id.startsWith(cellIdPrefix)) {
      return node;
    }

    node = node.parentNode as Element;
  }

  return null;
}

function parseCellLocation(cellId:string) {
  const parts = cellId.split('-');

  let column = -1;
  let row = -1;

  if (parts.length < 2) {
    // Should never happen
    return { column, row };
  }
  column = parseInt(parts[parts.length - 1]);
  row = parseInt(parts[parts.length - 2]);

  return { column, row };
}


export default function useTableRowSelection(rootNodeId:string, tableId:string, onRowDoubleClick:any, onRowClick:any) {
  const observer = useRef<MutationObserver|null>(null);
  const selectedRow = useRef(-1);

  useEffect(() => {
    initialize();

    return uninitialize;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function initialize() {
    initializeObserver();

    document.addEventListener('dblclick', onDoubleClick);
    document.addEventListener('click', onClick);
  }

  function uninitialize() {
    document.removeEventListener('dblclick', onDoubleClick);
    document.removeEventListener('click', onClick);
    uninitializeObserver();
  }

  function initializeObserver() {
    if (observer && observer.current) {
      return;
    }

    const targetNode = document.getElementById(rootNodeId);
    if (!targetNode) {
      return;
    }

    const observerConfig = {
      attributes: true,
      childList: true,
      subtree: true,
    };

    observer.current = new MutationObserver(observe);
    observer.current.observe(targetNode, observerConfig);
  }

  function uninitializeObserver() {
    if (observer && observer.current) {
      observer.current.disconnect();
    }
  }

  function observe() {
    doSelectTableRow(selectedRow.current);
  }

  function onDoubleClick(e:MouseEvent) {
    const clickedCell = findClickedCell(e, tableId);
    if (clickedCell) {
      const {row} = parseCellLocation(clickedCell.id);

      selectTableRow(row);

      if (onRowDoubleClick) {
        onRowDoubleClick(row);
      }
    }
  }

  function onClick(e:MouseEvent) {
    const clickedCell = findClickedCell(e, tableId);
    if (clickedCell) {
      const {row} = parseCellLocation(clickedCell.id);

      selectTableRow(row);

      if (onRowClick) {
        onRowClick(row);
      }
    }
  }

  function doSelectTableRow(index:number) {
    if (index < 0) {
      return false;
    }

    const table = document.getElementById(tableId);
    if (!table) {
      return;
    }

    const rowId = `${tableId}-row-${index}`;
    const row = document.getElementById(rowId);
    if (!row) {
      // Invalid index
      return false;
    }

    const currentRowSelections = table.querySelectorAll('.data-row-selected');
    if (currentRowSelections && currentRowSelections.length > 0) {
      if (currentRowSelections[0].id === rowId) {
        // Required row already selected
        return false;
      } else {
        currentRowSelections[0].classList.remove('data-row-selected');
        currentRowSelections[0].classList.add('data-row-selectable');
      }
    }

    if (row) {
      row.classList.remove('data-row-selectable');
      row.classList.add('data-row-selected');
      return true;
    }

    return false;
  }

  function selectTableRow(row:number) {
    selectedRow.current = row;
    doSelectTableRow(row);
  }

  function getSelectedRow() {
    return selectedRow.current;
  }

  return {
    selectTableRow,
    getSelectedRow,
  };
}
